/* AUTO-SPLIT from Documents.jsx (Iteration 14).
 * Per-path document file. Shared primitives are imported from ./_shared.
 * Do not edit Documents.jsx for these components — edit here.
 */
import React from "react";
import { Landmark } from "lucide-react";
import { fmtCurrency, fmtDate, numberToWords } from "../../lib/format";
import { FIRM } from "../../lib/firm";
import { calc, DocFooter, Paper, hashStr, makeSerial, TreasuryLogo, BarCode, BankersAcceptance, SignatureLine, findSig, InlineNotaryBlock, ChainOfTitle, acceptingBankPayee, GoldFoilSeal } from "./_shared";

// Compact wire-instructions box for the Bank Note purchase block (renders blank underscores when empty).
const WireBox = ({ label, bank, routing, swift, acctName, acctNo }) => {
  const L = (v) => (v ? <strong>{v}</strong> : <span style={{ letterSpacing: "0.5px" }}>__________</span>);
  return (
    <div style={{ flex: 1, border: "1px solid #C9C9C0", borderRadius: 3, padding: "4px 6px", fontSize: "6.8pt", lineHeight: 1.35 }}>
      <div style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--boa-blue, #0B4636)", marginBottom: 1, fontSize: "6.4pt" }}>{label}</div>
      <div>Bank: {L(bank)} &nbsp; ABA: {L(routing)} &nbsp; SWIFT: {L(swift)}</div>
      <div>Acct Name: {L(acctName)} &nbsp; Acct No.: {L(acctNo)}</div>
    </div>
  );
};

export const Doc0SubjectNote = React.forwardRef(({ d }, ref) => {
  const { maturity } = calc(d);
  const serial = makeSerial("TH-CP-" + (d.makerName || "X").replace(/\s/g, "").slice(0, 6).toUpperCase());
  const cusip = "TH" + String(Math.abs((d.faceValue || 0) * 13 + (d.couponRate || 0) * 7)).slice(0, 7).padStart(7, "0");
  const verifyCode = `${serial} · CUSIP ${cusip} · ${fmtDate(d.maturityDate)}`;
  return (
    <Paper
      ref={ref}
      className="note-instrument"
      footer={<DocFooter label="Doc 00 — Subject Note · UCC § 3-104 Negotiable Instrument (Senior Negotiable Instrument)" />}
    >
      <div className="note-head">
        <div className="note-head-grid">
          <TreasuryLogo name={d.treasuryHouseName} tagline={d.treasuryHouseTagline || "Senior Negotiable Commercial Paper · Series A"} />
          <div style={{ textAlign: "right", fontFamily: "Manrope, sans-serif" }}>
            <div style={{ fontSize: "8.5pt", textTransform: "uppercase", letterSpacing: "0.24em", color: "var(--boa-red)", fontWeight: 700 }}>
              UCC Article 3 · Negotiable Instrument
            </div>
            <div style={{ fontSize: "10pt", color: "#5C5C5C", marginTop: 4 }}>
              Issue Date · {fmtDate(d.executionDate)}
            </div>
            <div style={{ fontSize: "10pt", color: "#5C5C5C" }}>
              Maturity · {fmtDate(d.maturityDate)}
            </div>
          </div>
        </div>
        <div className="treasury-stripe"><span /><span /></div>
        <div className="note-serial">
          <span><span className="pill">SERIAL</span>{serial}</span>
          <span><span className="pill">CUSIP</span>{cusip}</span>
          <span><span className="pill">SERIES</span>A · 2026</span>
        </div>
      </div>

      <div className="note-body">
        <h1 className="doc-title" style={{ fontSize: "22pt", marginBottom: 2, color: "var(--boa-blue)" }}>Promissory Note</h1>
        <h2 className="doc-sub" style={{ marginBottom: 18 }}>
          Unconditional · Payable to Order · Article 3, Uniform Commercial Code
        </h2>

        <div className="face-amount-band">
          <div className="num">{fmtCurrency(d.faceValue)}</div>
          <div className="stars">★ ★ ★</div>
          <div className="words">{numberToWords(d.faceValue).toUpperCase()}</div>
        </div>

        <table className="kv-table" style={{ marginBottom: 14 }}>
          <tbody>
            <tr><td>Maker / Obligor</td><td>{d.makerName}{d.makerSsn ? ` · SSN •••-••-${String(d.makerSsn).replace(/\D/g, "").slice(-4)}` : ""}<div style={{ fontSize: "8pt", color: "#555", marginTop: 1 }}>by &amp; through Seutrade Financial Services</div></td></tr>
            <tr><td>Maker Address</td><td>{d.makerAddress}</td></tr>
            <tr><td>Payable to the Order of</td><td>{acceptingBankPayee(d)} — Discounting / Accepting Bank</td></tr>
            <tr><td>Arranger / Broker Direct</td><td>{FIRM.name}<div style={{ fontSize: "8pt", color: "#555", marginTop: 1 }}>{FIRM.address}</div></td></tr>
            <tr><td>Coupon</td><td>{d.couponRate.toFixed(2)}% per annum · 365-day basis</td></tr>
            <tr><td>Maturity Value</td><td>{fmtCurrency(maturity)}</td></tr>
            <tr><td>Place of Payment</td><td>{d.acceptanceBankName}</td></tr>
            <tr><td>Governing Law</td><td>State of {d.governingLaw} · UCC Articles 3 &amp; 9</td></tr>
          </tbody>
        </table>

        <p style={{ marginTop: 6 }}>
          <span className="clause-title">FOR VALUE RECEIVED,</span> the undersigned <strong>{d.makerName}</strong>, {d.makerSsn ? <>an individual and obligor of record (SSN •••-••-{String(d.makerSsn).replace(/\D/g, "").slice(-4)}), Maker by and through <strong>Seutrade Financial Services</strong></> : <>a corporation duly organized and existing under the laws of the State of {d.governingLaw}</>} (the <em>"Maker"</em>), hereby unconditionally <strong>PROMISES TO PAY TO THE ORDER OF</strong> <strong>{acceptingBankPayee(d)}</strong> (the <em>"Discounting / Accepting Bank"</em> and Payee), this Note being arranged and placed directly by <strong>{FIRM.name}</strong> as broker/arranger of record, with the net proceeds to be applied by the Discounting / Accepting Bank to pay the Borrower's counterparty (current lender/servicer) on behalf of, and in satisfaction or reinstatement of, the Borrower's debt obligation / loan account; payable to the Payee or its registered assigns, holder, or bearer in due course, the principal sum of <span className="legal-words">{numberToWords(d.faceValue).toUpperCase()}</span> <strong>({fmtCurrency(d.faceValue)})</strong>, in lawful currency of the United States of America, together with interest at the fixed rate of <strong>{d.couponRate.toFixed(2)}% per annum</strong>, all payable in a single bullet payment of <strong>{fmtCurrency(maturity)}</strong> on the Maturity Date stated above.
        </p>

        <ol style={{ marginTop: 8 }}>
          <li>
            <span className="clause-title">Negotiability (UCC § 3-104).</span> This instrument satisfies each requirement of UCC § 3-104(a): (i) an unconditional written promise; (ii) for a fixed amount of money; (iii) payable to order on a date certain; (iv) contains no undertakings or instructions beyond payment of money; and (v) is signed by the Maker. The instrument is intended to be a <strong>negotiable instrument</strong> freely transferable by endorsement and delivery, or by delivery alone if endorsed in blank.
          </li>
          <li>
            <span className="clause-title">Holder in Due Course (§ 3-302).</span> Any subsequent transferee taking this Note for value, in good faith, and without notice of defense, claim in recoupment, or that the instrument is overdue, shall acquire the rights of a <strong>Holder in Due Course</strong> and shall take this Note free from all personal defenses of the Maker.
          </li>
          <li>
            <span className="clause-title">Waivers (§§ 3-503, 3-504).</span> Maker and all endorsers, sureties, and guarantors hereby <strong>WAIVE</strong> presentment for payment, demand, notice of dishonor, notice of non-payment, protest, and notice of protest; and consent to extensions of time and substitutions of collateral without releasing the liability of any party hereto.
          </li>
          <li>
            <span className="clause-title">Senior Obligation; Pari Passu.</span> Maker represents that this Note constitutes a senior, direct, unsubordinated obligation of Maker, ranking <em>pari passu</em> with all other senior unsecured obligations of Maker, and that no contractual lien, set-off, counterclaim, or defense exists against payment.
          </li>
          <li>
            <span className="clause-title">Bullet Payment at Maturity.</span> Principal and accrued interest shall be paid in a single bullet sum on the Maturity Date by wire transfer of immediately available funds to the account designated by the holder in writing not less than three (3) Business Days prior to Maturity.
          </li>
          <li>
            <span className="clause-title">Acceleration on Default.</span> Upon (i) failure to pay any sum due hereunder; (ii) insolvency, assignment for the benefit of creditors, or bankruptcy of Maker; or (iii) any material misrepresentation, the entire unpaid balance shall, at the option of the holder, become immediately due and payable, together with default interest at the lesser of the maximum lawful rate or {Math.max(d.couponRate + 4, 12).toFixed(2)}% per annum and reasonable collection costs and attorneys' fees.
          </li>
          <li>
            <span className="clause-title">Transfer &amp; Endorsement.</span> This Note may be transferred only by endorsement of the holder and delivery, or by delivery alone if endorsed in blank, pursuant to UCC §§ 3-201 and 3-204. All Allonges (UCC § 3-204(a)) physically affixed hereto are deemed integral parts of this Note.
          </li>
          <li>
            <span className="clause-title">Lost / Destroyed Instrument.</span> In the event this Note is lost, stolen, or destroyed, Maker shall execute and deliver a replacement note of like tenor pursuant to UCC § 3-309 upon receipt of customary affidavit and reasonable indemnity from the holder.
          </li>
          <li>
            <span className="clause-title">Securities Law; Private Placement.</span> This Note is offered and sold in reliance on the exemption from registration under <strong>Section 4(a)(2) and/or Regulation D</strong> of the <strong>Securities Act of 1933</strong>, as amended; it has <strong>not been registered</strong> with the U.S. Securities and Exchange Commission or any state securities authority, and may not be resold or transferred except pursuant to an effective registration statement or an available exemption from registration. This is a private commercial transaction between the parties and is <strong>not a deposit, is not FDIC-insured, and is not an obligation of, or guaranteed by, the United States, the Federal Reserve, or any government agency.</strong>
          </li>
        </ol>

        <p style={{ fontStyle: "italic", marginTop: 14 }}>
          <strong>IN WITNESS WHEREOF,</strong> the Maker has caused this Note to be duly executed and delivered on the Date of Issue first above written, intending to be legally bound hereby.
        </p>

        <div className="sig-block">
          <div className="sig-col">
            <p><strong>MAKER:</strong></p>
            <p>{d.makerName}</p>
            <div className="sig-line">By: ______________________________</div>
            <div className="sig-line">Title: Authorized Officer</div>
            <div className="sig-line">Date: {fmtDate(d.executionDate)}</div>
          </div>
          <div className="sig-col">
            <p><strong>COUNTERSIGNED · PAYING AGENT:</strong></p>
            <p>{d.acceptanceBankName}</p>
            <div className="sig-line">By: {d.acceptanceOfficer}</div>
            <div className="sig-line">Title: {d.acceptanceOfficerTitle}</div>
            <div className="sig-line">Date: {fmtDate(d.executionDate)}</div>
          </div>
        </div>

        {/* Traditional Private Negotiable Instrument on the Note */}
        <BankersAcceptance d={d} instrumentRef={<>this Promissory Note dated {fmtDate(d.executionDate)} in the face amount of {fmtCurrency(d.faceValue)}</>} />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
          <GoldFoilSeal label="Executed Under Seal" />
        </div>

        <div className="endorsement-box">
          <div className="endorse-title">Endorsements / Chain of Title (Allonge Permitted — § 3-204)</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "12pt" }}>
            Pay to the order of: ______________________________________________&nbsp;&nbsp;
            By: __________________ &nbsp; Date: __________
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "12pt", marginTop: 8 }}>
            Pay to the order of: ______________________________________________&nbsp;&nbsp;
            By: __________________ &nbsp; Date: __________
          </div>
        </div>

        {/* Verification barcode */}
        <BarCode value={verifyCode} width={420} />

        <div style={{ marginTop: 16, fontFamily: "'IBM Plex Mono', monospace", fontSize: "8.5pt", color: "#5C5C5C", textAlign: "center", letterSpacing: "0.14em", textTransform: "uppercase" }}>
          Authority · UCC §§ 3-104, 3-302, 3-409, 3-410 · 9-109(a)(3) · 9-309(4)<br />
          Securities Act of 1933 § 4(a)(2) / Regulation D Private Placement · Not Registered · Transfer Restricted · Reves v. Ernst &amp; Young, 494 U.S. 56 (1990)
        </div>
        <div style={{ marginTop: 8, fontFamily: "'IBM Plex Mono', monospace", fontSize: "9pt", color: "#5C5C5C", textAlign: "center", letterSpacing: "0.18em", textTransform: "uppercase" }}>
          — Original Wet-Ink Instrument · Senior Negotiable Commercial Paper · Do Not Photocopy —
        </div>
      </div>
    </Paper>
  );
});

// ============ DOCUMENT 00-A: DISCOUNT DRAFT (BoA Certified Bank Draft Style) ============

export const Doc0DiscountDraft = React.forwardRef(({ d }, ref) => {
  const discountPrice = d.purchasePrice;
  const pctOfFace = d.faceValue ? (discountPrice / d.faceValue) * 100 : 0;
  const serial = makeSerial("TH-DD-" + String(d.faceValue).slice(0, 6));
  const draftNo = String(Math.abs((d.faceValue || 0) + (d.purchasePrice || 0))).slice(-6).padStart(6, "0");
  const verifyCode = `${serial} · DRAFT ${draftNo} · ${fmtDate(d.saleClosingDate)}`;

  return (
    <Paper
      ref={ref}
      className="draft-check"
      footer={<DocFooter label="Doc 00-A — Private Negotiable Instrument (UCC §§ 3-104, 3-409, 3-410)" />}
    >
      {/* Edge-to-edge BoA-style banner (no top space, no border) */}
      <div className="draft-banner">
        <div className="bank-mark">
          <div className="icon"><Landmark size={22} strokeWidth={2} /></div>
          <div className="name">
            {d.treasuryHouseName || "Treasury House"}
            <span className="sub">{d.treasuryHouseTagline || "Discount Draft · Series A"}</span>
          </div>
        </div>
        <div className="instrument-tag">
          Senior Negotiable Instrument
          <span style={{ display: "block", fontSize: "15pt", fontWeight: 800, letterSpacing: "0.04em", color: "#fff", lineHeight: 1.05, margin: "2px 0 3px" }}>SENIOR NEGOTIABLE NOTE</span>
          <span className="small">Negotiable promissory note — issued by a private lender · UCC §§ 3-409 / 3-410</span>
        </div>
        <div className="draft-no">
          Draft No.
          <span className="big">{draftNo}</span>
          Serial · {serial}
          {d.mintedSerial ? <span style={{ display: "block", marginTop: 2, color: "#E4C97A", fontWeight: 700 }}>Bank Note Serial · {d.mintedSerial}</span> : null}
        </div>
      </div>

      <div className="draft-body">
        <div className="draft-row1">
          <div className="drawer">
            <div className="label">Drawer · Holder of Subject Note</div>
            <div className="name">
              {String(d.borrowerName || "").replace(/\s*-\s*FINANCIAL SERVICES\s*$/i, "")}
              {/\bFINANCIAL SERVICES\b/i.test(d.borrowerName || "") && (
                <span style={{ display: "block", fontSize: "8pt", letterSpacing: "0.14em", color: "#B8860B", fontWeight: 600, marginTop: 1 }}>Financial Services</span>
              )}
            </div>
            <div className="meta">{d.borrowerAddress}</div>
            <div className="meta" style={{ fontSize: "7.5pt", marginTop: 2 }}>
              Registration / EIN No. <strong>{d.regNo || FIRM.ein}</strong>
              {d.intlRegNo ? <> &nbsp;·&nbsp; Intl. Reg. No. <strong>{d.intlRegNo}</strong></> : null}
            </div>
          </div>
          <div className="draft-date">
            <div className="label">Date Drawn</div>
            <div className="value">{fmtDate(d.saleClosingDate)}</div>
            <div style={{ marginTop: 6, fontSize: "9pt" }}>Payable: <strong style={{ color: "var(--boa-blue)" }}>At {d.termDays} days' sight (usance) · {fmtDate(d.maturityDate)}</strong></div>
          </div>
        </div>

        <div className="pay-line">
          <div className="pay-label">Pay to the<br />Order of</div>
          <div className="pay-name">{acceptingBankPayee(d)}</div>
          <div className="pay-label" style={{ paddingBottom: 12 }}>
            U.S. $
          </div>
          <div className="pay-amount">
            {Number(discountPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        <div className="amount-words">{numberToWords(discountPrice)}</div>

        <div className="reference-block">
          <div className="ref-label">Drawn Under Reference (Secure Payload Delivery · Encrypted Data Exchange)</div>
          Draw down under that certain <strong>Note Purchase &amp; Assignment Agreement</strong> dated <strong>{fmtDate(d.saleClosingDate)}</strong> between {d.borrowerName} (Seller) and {d.buyerName} (Buyer); and charge to the account of <strong>{d.makerName}</strong> pursuant to the <strong>Promissory Note dated {fmtDate(d.executionDate)}</strong> in the original face principal amount of {fmtCurrency(d.faceValue)}, maturing {fmtDate(d.maturityDate)} (the <em>"Subject Note"</em>). This Draft is presented for the discounted consideration of <strong>{pctOfFace.toFixed(2)}%</strong> of face value and is settled by institutional wire (Fedwire / CHIPS) via Secure Payload Delivery · Encrypted Data Exchange per the closing packet.
        </div>

        <div className="drawee-block">
          <div>
            <div className="block-label">Drawee · Paying Bank (Acceptor)</div>
            <div className="block-value">
              <strong>{d.acceptanceBankName || "[ Accepting Bank — to be designated ]"}</strong><br />
              {d.acceptanceDeskAddress}<br />
              Upon acceptance, the Drawee becomes the <strong>Acceptor</strong> of this time draft.
            </div>
          </div>
          <div>
            <div className="block-label">For — Discount Purchase of Subject Note</div>
            <div className="block-value">
              Maker: <strong>{d.makerName}</strong><br />
              Face: {fmtCurrency(d.faceValue)} &nbsp;·&nbsp; Discount: {(100 - pctOfFace).toFixed(2)}%<br />
              Maturity Value: {fmtCurrency(calc(d).maturity)}
            </div>
          </div>
        </div>

        <div className="terms">
          <div className="terms-label">Terms of this Draft (Private Negotiable Instrument · UCC §§ 3-104, 3-409, 3-410)</div>
          At <strong>{d.termDays} days' sight</strong>, on the Maturity Date stated above, pay to the order of <strong>{acceptingBankPayee(d)}</strong> the sum certain stated above. This is a <strong>Private Negotiable Instrument</strong> drawn under and secured by the <strong>Promissory Note dated {fmtDate(d.executionDate)}</strong> in the face amount of {fmtCurrency(d.faceValue)} (the <em>"Subject Note"</em>), creating a <strong>deferred-payment obligation</strong>. Upon acceptance by <strong>{d.acceptanceBankName || "the Accepting Bank"}</strong> (the <em>"Acceptor"</em>), this Draft constitutes an <strong>accepted, bank-credit-enhanced time draft</strong> — a primary, unconditional commercial-financing instrument eligible for discount and rediscount in the secondary market. Presentment shall be made via <strong>Secure Payload Delivery · Encrypted Data Exchange</strong>, and/or may be delivered via secure file transmission or certified/registered mail carrier to the counterparty. <strong>Authorization.</strong> The Maker/Borrower hereby authorizes and directs the Accepting/Discounting Bank, upon acceptance and/or discount of this instrument, to (i) remit the net proceeds directly to the Borrower's counterparty (current lender/servicer) — by Fedwire or by mailed certified/official bank check drawn to the counterparty for credit to the Borrower's loan/account — in payoff or reinstatement of the referenced account/loan, and (ii) execute, issue, transmit, and mail any related payoff, release, or notice to such counterparty on the Borrower's behalf. This Draft satisfies UCC § 3-104: (i) an unconditional order to pay, (ii) a sum certain, (iii) a definite time of payment (usance / maturity), (iv) the Drawee / Acceptor clearly identified, and (v) the authorized signature of the Drawer. This Bank Note is a negotiable banker's acceptance <strong>eligible for presentment, acceptance, discount, and collection at any accepting financial institution</strong> through the ordinary bank-collection channels of <strong>UCC Articles 3 and 4</strong>.
        </div>

        <div className="draft-sig">
          <div>
            {findSig(d.signatures, "seller")
              ? <SignatureLine sig={findSig(d.signatures, "seller")} defaultName={d.signerName} />
              : <div className="sig">{d.signerName}</div>}
            <div className="sig-cap">Drawer — Authorized Signature ({d.signerTitle})</div>
          </div>
          <div>
            {findSig(d.signatures, "acceptance")
              ? <SignatureLine sig={findSig(d.signatures, "acceptance")} defaultName={d.acceptanceOfficer} />
              : <div className="sig">{d.acceptanceOfficer}</div>}
            <div className="sig-cap">Countersigned — {d.acceptanceBankName}</div>
          </div>
        </div>

        {/* Traditional Private Negotiable Instrument on the Draft */}
        <BankersAcceptance d={d} instrumentRef={<>this Discount Draft No. {draftNo} dated {fmtDate(d.saleClosingDate)}</>} />

        {/* Purchase & Acceptance by the new purchaser (secondary-market buyer) */}
        <div style={{ border: "1.5px solid var(--boa-blue, #0B4636)", borderRadius: 4, padding: "6px 10px", marginTop: 8, position: "relative" }} data-testid="ss-draft-purchase-block">
          <div style={{ position: "absolute", right: 6, top: 2 }}><GoldFoilSeal size={46} label="" /></div>
          <div style={{ fontWeight: 700, fontSize: "8.5pt", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--boa-blue, #0B4636)", marginBottom: 2 }}>
            Purchase &amp; Acceptance by Purchaser
          </div>
          <p style={{ fontSize: "7.5pt", lineHeight: 1.4, margin: "0 0 4px", paddingRight: 52 }}>
            For value received, the undersigned <strong>Purchaser</strong> <strong>purchases and accepts</strong> this private banker's
            acceptance bank note from the Holder/Seller and takes it by negotiation without recourse under UCC § 3-415 for
            {" "}<strong>{fmtCurrency(d.purchasePrice)}</strong> ({pctOfFace.toFixed(2)}% of face), becoming the holder entitled to enforce it at maturity.
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              {findSig(d.signatures, "buyer")
                ? <SignatureLine sig={findSig(d.signatures, "buyer")} defaultName={d.buyerSigner} />
                : <div className="sig-line" style={{ fontSize: "8pt" }}>By: ______________________________</div>}
              <div style={{ fontSize: "7.5pt", color: "#444", marginTop: 1 }}>Purchaser — {d.buyerName}{d.buyerTitle ? `, ${d.buyerTitle}` : ""}</div>
            </div>
            <div style={{ width: 150, fontSize: "7.5pt", color: "#444" }}>Settlement: {fmtDate(d.saleClosingDate)}</div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }} data-testid="ss-draft-purchaser-wires">
            <WireBox label="Purchaser Settlement — Outbound Wire (Purchase Price)" bank={d.purWireBank} routing={d.purWireRouting} swift={d.purWireSwift} acctName={d.purWireAcctName} acctNo={d.purWireAcctNo} />
            <WireBox label="Payable to Purchaser at Maturity" bank={d.payWireBank} routing={d.payWireRouting} swift={d.payWireSwift} acctName={d.payWireAcctName} acctNo={d.payWireAcctNo} />
          </div>
        </div>

        {/* Verification barcode */}
        <BarCode value={verifyCode} width={420} />
        {d.verifyQr ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 4 }} data-testid="ss-draft-verify-qr">
            <img src={d.verifyQr} alt="Verify" style={{ width: 54, height: 54 }} />
            <div style={{ fontSize: "7pt", color: "#333", lineHeight: 1.4 }}>
              <strong>Scan to verify this Bank Note</strong><br />
              Serial {d.mintedSerial} · independently verifiable by the purchaser
            </div>
          </div>
        ) : null}
        <div style={{ marginTop: 3, textAlign: "center", fontFamily: "'IBM Plex Mono', monospace", fontSize: "7pt", letterSpacing: "0.1em", color: "#555" }} data-testid="ss-draft-collection-line">
          ⑈ REF {draftNo} ⑈ &nbsp; REG/EIN {d.regNo || FIRM.ein} &nbsp; FOR COLLECTION THROUGH NORMAL BANK CHANNELS · UCC ARTS. 3 &amp; 4
        </div>
      </div>
    </Paper>
  );
});

// ============ DOCUMENT 1: Master Credit Facility Promissory Note ============

export const Doc6PurchaseAssignment = React.forwardRef(({ d }, ref) => {
  const c = calc(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc 06 — Note Purchase & Assignment Agreement (Discount Sale)" />}>
      <h2 className="doc-sub">Document 06 · Discount Sale Package</h2>
      <h1 className="doc-title">Note Purchase &amp; Assignment Agreement</h1>
      <h2 className="doc-sub">Sale of Promissory Note at Discount · with Allonge &amp; Notice to Buyer</h2>
      <hr className="doc-rule" />

      <ChainOfTitle d={d} />

      <p>
        This Note Purchase and Assignment Agreement (the <em>"Purchase Agreement"</em>) is entered into as of <strong>{fmtDate(d.saleClosingDate)}</strong>, by and between <strong>{d.borrowerName}</strong>, a {d.borrowerState} limited liability company (<em>"Seller"</em>), and <strong>{d.buyerName}</strong> (<em>"Buyer"</em>).
      </p>

      <p style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 18 }}>Article I — Sale of the Note</p>

      <ol>
        <li>
          <span className="clause-title">Subject Note.</span> Seller is the sole holder and beneficial owner of that certain UCC Article 3 Promissory Note dated <strong>{fmtDate(d.executionDate)}</strong>, executed by <strong>{d.makerName}</strong> (<em>"Maker"</em>) payable to the order of Seller in the original face principal amount of <strong>{fmtCurrency(d.faceValue)}</strong>, bearing interest at <strong>{d.couponRate.toFixed(2)}%</strong> per annum and maturing on <strong>{fmtDate(d.maturityDate)}</strong> (the <em>"Subject Note"</em>).
        </li>
        <li>
          <span className="clause-title">Purchase &amp; Sale.</span> Subject to the terms of this Purchase Agreement, Seller hereby sells, assigns, transfers, conveys, and delivers to Buyer, and Buyer hereby purchases from Seller, all of Seller's right, title, and interest in and to the Subject Note, including all principal, interest, and proceeds thereof, free and clear of all liens and encumbrances.
        </li>
        <li>
          <span className="clause-title">Purchase Price.</span> The aggregate purchase price for the Subject Note shall be <span className="legal-words">{numberToWords(d.purchasePrice).toUpperCase()}</span> <strong>({fmtCurrency(d.purchasePrice)})</strong> (the <em>"Purchase Price"</em>), payable by Buyer to Seller by wire transfer of immediately available funds at Closing.
        </li>
        <li>
          <span className="clause-title">Discount Acknowledgement.</span> The parties acknowledge that the Purchase Price represents a discount to the Subject Note's maturity value. The economic terms are summarized as follows:

          <table className="kv-table" style={{ marginTop: 8 }}>
            <tbody>
              <tr><td>Face Principal</td><td>{fmtCurrency(d.faceValue)}</td></tr>
              <tr><td>Stated Coupon</td><td>{d.couponRate.toFixed(2)}% / {d.termDays} days</td></tr>
              <tr><td>Maturity Value</td><td>{fmtCurrency(c.maturity)}</td></tr>
              <tr><td>Purchase Price</td><td>{fmtCurrency(d.purchasePrice)}</td></tr>
              <tr><td>Total Discount</td><td>{fmtCurrency(c.discount)} ({c.discountPct.toFixed(2)}%)</td></tr>
              <tr><td>Buyer Holding Yield</td><td>{c.holdingYield.toFixed(2)}%</td></tr>
              <tr><td>Annualized Yield</td><td>{c.annualYield.toFixed(2)}%</td></tr>
            </tbody>
          </table>
        </li>
      </ol>

      <p style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 6 }}>Article II — Closing &amp; Deliveries</p>

      <ol start={5}>
        <li>
          <span className="clause-title">Closing.</span> Closing shall occur on <strong>{fmtDate(d.saleClosingDate)}</strong>. At Closing, Seller shall deliver to Buyer (i) the original wet-ink executed Subject Note bearing the Allonge attached as <em>Exhibit A</em>, (ii) an executed Bill of Sale and Assignment in the form of <em>Exhibit B</em>, and (iii) the Notice to Buyer in the form of <em>Exhibit C</em>.
        </li>
        <li>
          <span className="clause-title">Representations &amp; Warranties.</span> Seller represents and warrants that: (a) Seller is the sole legal and equitable owner of the Subject Note; (b) the Subject Note is genuine, enforceable, and free of any defenses, set-offs, or counterclaims known to Seller; (c) no portion of the Subject Note has been previously sold, assigned, pledged, or hypothecated to any third party (or, if pledged, such pledge has been or will be released contemporaneously with Closing); and (d) Seller has full corporate authority to consummate this sale.
        </li>
        <li>
          <span className="clause-title">Without Recourse.</span> Except as expressly provided herein, this sale is made <strong>WITHOUT RECOURSE</strong> to Seller for the credit of the Maker, and Buyer shall bear all credit risk relating to Maker's performance under the Subject Note.
        </li>
        <li>
          <span className="clause-title">Further Assurances.</span> Each party agrees to execute and deliver such further instruments as may reasonably be necessary to consummate and give full effect to the transactions contemplated hereby.
        </li>
        <li>
          <span className="clause-title">Governing Law.</span> This Purchase Agreement shall be governed by, and construed in accordance with, the laws of the State of {d.governingLaw}, without giving effect to conflict-of-laws principles.
        </li>
        <li>
          <span className="clause-title">Regulatory Compliance &amp; Authority.</span> The parties acknowledge that the transactions contemplated hereby are governed by and conducted in compliance with: (a) <strong>UCC Article 3</strong> (Negotiable Instruments) §§ 3-104, 3-201, 3-204, 3-302, 3-409, 3-410, 3-415 of the State of {d.governingLaw}; (b) <strong>UCC Article 9</strong> (Secured Transactions) § 9-109(a)(3), which expressly extends Article 9 to the outright <em>sale</em> of promissory notes, and §§ 9-203, 9-309(4) (automatic perfection on sale of a promissory note), and 9-406 (account-debtor notice); (c) <strong>Section 3(a)(3) of the Securities Act of 1933</strong> (15 U.S.C. § 77c(a)(3)) and the <em>Reves v. Ernst &amp; Young</em>, 494 U.S. 56 (1990) "family-resemblance" test, under which a commercial promissory note of nine (9) months or less maturity issued for current commercial transactions is exempt from federal securities registration; (d) <strong>Federal Reserve Regulation A</strong> (12 C.F.R. Part 201) and historical eligibility standards for bankers' acceptances; (e) <strong>Bank Secrecy Act</strong> / <strong>USA PATRIOT Act</strong> AML and KYC requirements (31 U.S.C. § 5311 et seq.); and (f) <strong>OFAC sanctions regulations</strong> (31 C.F.R. Chapter V).
        </li>
        <li>
          <span className="clause-title">OFAC / AML Representations.</span> Each party represents and warrants that (i) neither it nor any of its principals, beneficial owners, or affiliates is a Specially Designated National or otherwise the subject of sanctions administered by the U.S. Department of the Treasury's Office of Foreign Assets Control (OFAC); (ii) the funds used to consummate this transaction have been derived from lawful sources and are not the proceeds of any unlawful activity; and (iii) it has satisfied all applicable customer-identification (CIP) and beneficial-ownership rules under 31 C.F.R. § 1010.230.
        </li>
        <li>
          <span className="clause-title">Tax Allocation; 1099-OID.</span> Buyer and Seller agree that the discount between the Purchase Price and the maturity value of the Subject Note shall be reported as Original Issue Discount or market discount as appropriate under <strong>I.R.C. §§ 1271–1275</strong>. Buyer shall be responsible for issuing or causing to be issued any required <strong>IRS Form 1099-OID</strong> or 1099-INT at maturity. Seller shall furnish a properly completed <strong>IRS Form W-9</strong> (or W-8 series, as applicable) at Closing.
        </li>
        <li>
          <span className="clause-title">No Recharacterization as Loan.</span> The parties intend, and shall treat for all tax, accounting, regulatory, and bankruptcy purposes, this transaction as a <strong>true sale</strong> and absolute conveyance of the Subject Note, and not as a loan, financing, pledge, or other secured transaction. Seller retains no equity of redemption, repurchase obligation, or right to reacquire the Subject Note.
        </li>
        <li>
          <span className="clause-title">Entire Agreement; Merger.</span> This Purchase Agreement, together with the Subject Note, the Allonge (Exhibit A), the Bill of Sale (Exhibit B), the Notice to Buyer (Exhibit C), the Closing Certificate (Exhibit D), the Incumbency Certificate (Exhibit E), and the Proceeds Direction Letter (Exhibit F), constitutes the entire agreement of the parties with respect to the subject matter hereof and supersedes all prior negotiations, representations, and understandings, whether written or oral. No modification shall be valid except in a writing signed by both parties.
        </li>
        <li>
          <span className="clause-title">Proceeds Direction.</span> The parties acknowledge that, by separate Proceeds Direction &amp; Disbursement Letter dated {fmtDate(d.saleClosingDate)} delivered at Closing (Exhibit F), Seller has irrevocably elected the account into which Buyer shall wire the Purchase Price. Buyer's compliance with that direction shall constitute full discharge of Buyer's payment obligation under this Purchase Agreement in accordance with UCC § 9-406(a).
        </li>
        <li>
          <span className="clause-title">Not a Loan, Line of Credit, or Extension of Credit.</span> The parties expressly and unambiguously characterize this transaction as a <strong>true sale</strong> of a negotiable instrument and not as a financing transaction. Nothing in this Purchase Agreement, the Private Negotiable Instrument Draft, the Proceeds Direction Letter, or any related Transaction Document shall be construed as: (a) a loan, advance, or extension of credit by Buyer to Seller, the Trust, the Trustee, or any designated counterparty; (b) a commitment, agreement, or obligation by Buyer to lend, to advance funds, or to make any future credit accommodation; (c) a line of credit, revolving credit facility, term loan, demand loan, or financing commitment of any kind, character, or duration; (d) a guarantee, surety, or accommodation by Buyer of any obligation owed by Seller to any third party; or (e) the establishment of a debtor-creditor relationship between Buyer and Seller. Buyer's sole capacity hereunder is that of a <strong>purchaser-for-value of a UCC Article 3 negotiable instrument</strong> in an arm's-length commercial transaction, with the rights of a Holder in Due Course under UCC § 3-302 upon delivery of the Subject Note and Allonge.
        </li>
      </ol>

      <div className="sig-block">
        <div className="sig-col">
          <p><strong>SELLER:</strong></p>
          <p>{d.borrowerName}</p>
          <SignatureLine sig={findSig(d.signatures, "seller")} defaultName={d.signerName} />
          <div className="sig-line">Title: {d.signerTitle || "Authorized Officer"}</div>
          {findSig(d.signatures, "trustee") && (
            <div style={{ marginTop: 16, paddingTop: 10, borderTop: "1px dotted #5C5C5C" }}>
              <p style={{ fontSize: "9.5pt", color: "#5C5C5C", marginBottom: 2 }}><strong>Co-Trustee (Witness &amp; Independent Affirmation):</strong></p>
              <SignatureLine sig={findSig(d.signatures, "trustee")} defaultName={d.cotrusteeName} />
              <div className="sig-line">Title: {d.cotrusteeTitle || "Independent Co-Trustee"}</div>
            </div>
          )}
        </div>
        <div className="sig-col">
          <p><strong>BUYER:</strong></p>
          <p>{d.buyerName}</p>
          <SignatureLine sig={findSig(d.signatures, "buyer")} defaultName={d.buyerSigner} />
          <div className="sig-line">Title: {d.buyerTitle || "Trustee"}</div>
        </div>
      </div>
      {findSig(d.signatures, "notary") && <InlineNotaryBlock d={d} signerName={d.borrowerName} />}
    </Paper>
  );
});

// ============ DOCUMENT 6-A: Allonge ============

export const Doc6AAllonge = React.forwardRef(({ d }, ref) => (
  <Paper ref={ref} footer={<DocFooter label="Doc 06-A — Allonge (Endorsement to Subject Note)" />}>
    <h2 className="doc-sub">Exhibit A · Discount Sale Package</h2>
    <h1 className="doc-title">Allonge</h1>
    <h2 className="doc-sub">Endorsement Affixed to the Original Subject Note</h2>
    <hr className="doc-rule" />

    <ChainOfTitle d={d} />

    <p>
      This Allonge is permanently affixed to, and forms an integral part of, that certain Promissory Note dated <strong>{fmtDate(d.executionDate)}</strong>, in the original face principal amount of <strong>{fmtCurrency(d.faceValue)}</strong>, executed by <strong>{d.makerName}</strong> and made payable to the order of <strong>{d.borrowerName}</strong> (the <em>"Subject Note"</em>).
    </p>

    <hr className="doc-rule-thin" />

    <p style={{ textAlign: "center", fontSize: "13pt", fontWeight: 600, letterSpacing: "0.02em", marginTop: 22 }}>
      PAY TO THE ORDER OF
    </p>

    <p style={{ textAlign: "center", fontSize: "16pt", fontWeight: 600, fontFamily: "'Cormorant Garamond', serif", marginTop: 4 }}>
      {d.buyerName}
    </p>

    <p style={{ textAlign: "center", fontStyle: "italic", marginTop: 14 }}>
      Without Recourse, Representation, or Warranty
      <br />— except as expressly set forth in the Note Purchase &amp; Assignment Agreement of even date —
    </p>

    <div style={{ marginTop: 60 }}>
      <p style={{ marginBottom: 6 }}><strong>SELLER / ENDORSER:</strong></p>
      <p>{d.borrowerName}</p>
      <SignatureLine sig={findSig(d.signatures, "seller")} defaultName={d.signerName} />
      <div className="sig-line">Title: {d.signerTitle || "Authorized Officer"}</div>
      <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
      {findSig(d.signatures, "trustee") && (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dotted #5C5C5C" }}>
          <p style={{ fontSize: "9.5pt", color: "#5C5C5C", marginBottom: 2 }}><strong>Co-Trustee (Witness):</strong></p>
          <SignatureLine sig={findSig(d.signatures, "trustee")} defaultName={d.cotrusteeName} />
          <div className="sig-line">Title: {d.cotrusteeTitle || "Independent Co-Trustee"}</div>
        </div>
      )}
    </div>
    {findSig(d.signatures, "notary") && <InlineNotaryBlock d={d} signerName={d.borrowerName} />}

    <p style={{ marginTop: 56, fontFamily: "'IBM Plex Mono', monospace", fontSize: "9pt", color: "#5C5C5C", textAlign: "center", letterSpacing: "0.16em", textTransform: "uppercase" }}>
      — This Allonge Must Be Physically Attached to the Original Note —
    </p>
  </Paper>
));

// ============ DOCUMENT 6-B: Bill of Sale ============

export const Doc6BBillOfSale = React.forwardRef(({ d }, ref) => (
  <Paper ref={ref} footer={<DocFooter label="Doc 06-B — Bill of Sale & Assignment of Note" />}>
    <h2 className="doc-sub">Exhibit B · Discount Sale Package</h2>
    <h1 className="doc-title">Bill of Sale &amp; Assignment</h1>
    <hr className="doc-rule" />

    <ChainOfTitle d={d} />

    <p>
      For good and valuable consideration in the sum of <strong>{fmtCurrency(d.purchasePrice)}</strong>, the receipt and sufficiency of which are hereby acknowledged, <strong>{d.borrowerName}</strong> (<em>"Assignor"</em>) does hereby <strong>SELL, ASSIGN, TRANSFER, AND CONVEY</strong> unto <strong>{d.buyerName}</strong> (<em>"Assignee"</em>), all of Assignor's right, title, and interest in and to the following:
    </p>

    <p style={{ marginLeft: 24, marginRight: 24 }}>
      That certain UCC Article 3 Promissory Note dated <strong>{fmtDate(d.executionDate)}</strong>, executed by <strong>{d.makerName}</strong> in the original face principal amount of <strong>{fmtCurrency(d.faceValue)}</strong>, bearing interest at {d.couponRate.toFixed(2)}% per annum and maturing on {fmtDate(d.maturityDate)} (the <em>"Subject Note"</em>), together with all instruments, accounts, contract rights, proceeds, and collections arising therefrom.
    </p>

    <p>
      Assignor warrants that it is the sole owner of the Subject Note and that the Subject Note is free of all liens, claims, and encumbrances at the time of this conveyance. The original wet-ink instrument, bearing the Allonge attached as <em>Exhibit A</em>, is hereby physically delivered to Assignee.
    </p>

    <p>
      This Bill of Sale is delivered pursuant to, and is governed by, the Note Purchase &amp; Assignment Agreement dated {fmtDate(d.saleClosingDate)} by and between Assignor and Assignee.
    </p>

    <div className="sig-block">
      <div className="sig-col">
        <p><strong>ASSIGNOR:</strong></p>
        <p>{d.borrowerName}</p>
        <SignatureLine sig={findSig(d.signatures, "seller")} defaultName={d.signerName} />
        <div className="sig-line">Title: {d.signerTitle || "Authorized Officer"}</div>
        <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
        {findSig(d.signatures, "trustee") && (
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dotted #5C5C5C" }}>
            <p style={{ fontSize: "9.5pt", color: "#5C5C5C", marginBottom: 2 }}><strong>Co-Trustee (Witness &amp; Independent Affirmation):</strong></p>
            <SignatureLine sig={findSig(d.signatures, "trustee")} defaultName={d.cotrusteeName} />
            <div className="sig-line">Title: {d.cotrusteeTitle || "Independent Co-Trustee"}</div>
          </div>
        )}
      </div>
      <div className="sig-col">
        <p><strong>ASSIGNEE (Receipt Acknowledged):</strong></p>
        <p>{d.buyerName}</p>
        <SignatureLine sig={findSig(d.signatures, "buyer")} defaultName={d.buyerSigner} />
        <div className="sig-line">Title: {d.buyerTitle || "Trustee"}</div>
        <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
      </div>
    </div>
    {findSig(d.signatures, "notary") && <InlineNotaryBlock d={d} signerName={d.borrowerName} />}
  </Paper>
));

// ============ DOCUMENT 6-C: Notice to Buyer (Confirmation of Assignment) ============

export const Doc6CNoticeToMaker = React.forwardRef(({ d }, ref) => {
  const c = calc(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc 06-C — Notice to Buyer · Confirmation of Sale & Holder Rights">
    </DocFooter>}>
      <h2 className="doc-sub">Exhibit C · Outright Note Sale Package</h2>
      <h1 className="doc-title">Notice to Buyer</h1>
      <h2 className="doc-sub">Confirmation of Sale, Assignment &amp; Holder-in-Due-Course Rights · UCC §§ 3-201, 3-302, 9-406</h2>
      <hr className="doc-rule" />

      <p><strong>Date:</strong> {fmtDate(d.saleClosingDate)}</p>
      <p><strong>From (Treasury House):</strong> {d.trustName}</p>
      <p><strong>To (Counterparty / Buyer):</strong> {d.buyerName}</p>
      <p><strong>Address:</strong> {d.buyerAddress}</p>
      <p style={{ marginTop: 12 }}><strong>Re:</strong> Confirmation of Sale &amp; Assignment of Subject Note dated {fmtDate(d.executionDate)}</p>

      <hr className="doc-rule-thin" />

      <p>
        The undersigned, <strong>{d.trustName}</strong> (the <em>"Trust"</em>, <em>"Seller"</em>, and Maker of the Subject Note referenced below), hereby formally notifies and confirms to <strong>{d.buyerName}</strong> (the <em>"Buyer"</em>) that, effective as of <strong>{fmtDate(d.saleClosingDate)}</strong> and pursuant to the Note Purchase &amp; Assignment Agreement of even date herewith, the Trust has irrevocably sold, assigned, transferred, conveyed, and endorsed (via the affixed Allonge) to Buyer all of its right, title, and interest in that certain UCC Article 3 Negotiable Promissory Note dated {fmtDate(d.executionDate)} in the original face principal amount of <strong>{fmtCurrency(d.faceValue)}</strong> (the <em>"Subject Note"</em>).
      </p>

      <ol>
        <li>
          <span className="clause-title">Buyer's Status.</span> Buyer is now the <strong>sole legal and beneficial Holder</strong> of the Subject Note and, upon taking the original wet-ink instrument and Allonge for value, in good faith, and without notice of any defense or claim, qualifies as a <strong>Holder in Due Course under UCC § 3-302</strong>, taking the Subject Note free from all personal defenses of the Trust as Maker.
        </li>
        <li>
          <span className="clause-title">Maturity Payment.</span> On <strong>{fmtDate(d.maturityDate)}</strong>, the Trust, as Maker of the Subject Note, shall pay to the order of Buyer (or such other holder as may then hold the Subject Note) the maturity sum of <span className="legal-words">{numberToWords(c.maturity).toUpperCase()}</span> <strong>({fmtCurrency(c.maturity)})</strong> by Fedwire / CHIPS transfer of immediately available funds to the account designated by Buyer in writing not less than three (3) Business Days prior to maturity.
        </li>
        <li>
          <span className="clause-title">Wire Instructions.</span> Buyer&apos;s wire / settlement instructions shall be delivered to the Trust in writing under separate cover. No beneficiary bank, routing number, or account number is designated in this Notice; the Buyer will furnish those instructions following execution.
        </li>
      </ol>

      <ol start={4}>
        <li>
          <span className="clause-title">No Defenses; No Set-Off.</span> The Trust represents and warrants that the Subject Note is genuine and enforceable, that no portion has been previously sold or pledged, and that no defense, set-off, counterclaim, or recoupment exists against payment.
        </li>
        <li>
          <span className="clause-title">Transferability.</span> Buyer may further negotiate, sell, pledge, or otherwise transfer the Subject Note by endorsement and delivery (or by delivery alone if endorsed in blank) without further consent of the Trust pursuant to UCC §§ 3-201 and 3-204.
        </li>
        <li>
          <span className="clause-title">Reliance.</span> This Notice may be relied upon by Buyer, by any successor Holder of the Subject Note, by Buyer's custodian, and by any audit, tax, or regulatory authority reviewing the closing of this transaction.
        </li>
      </ol>

      <div className="sig-block">
        <div className="sig-col">
          <p><strong>TREASURY HOUSE (Seller / Maker):</strong></p>
          <p>{d.trustName}</p>
          <SignatureLine sig={findSig(d.signatures, "buyer") || findSig(d.signatures, "trustee")} defaultName={d.trusteeName} />
          <div className="sig-line">Title: {d.trusteeTitle || "Trustee"}</div>
          <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
        </div>
        <div className="sig-col">
          <p><strong>COUNTERPARTY (Buyer · Acknowledged):</strong></p>
          <p>{d.buyerName}</p>
          <SignatureLine sig={findSig(d.signatures, "buyer")} defaultName={d.buyerSigner} />
          <div className="sig-line">Title: {d.buyerTitle || "Trustee"}</div>
          <div className="sig-line">Date Received: {fmtDate(d.saleClosingDate)}</div>
        </div>
      </div>
    </Paper>
  );
});


// ============ DOCUMENT 06-D: Seller's Closing Certificate ============

export const Doc6DClosingCert = React.forwardRef(({ d }, ref) => (
  <Paper ref={ref} footer={<DocFooter label="Doc 06-D — Seller's Closing Certificate (Bring-Down of Reps & Warranties)" />}>
    <h2 className="doc-sub">Exhibit D · Outright Note Sale Closing Package</h2>
    <h1 className="doc-title">Seller's Closing Certificate</h1>
    <h2 className="doc-sub">Bring-Down of Representations &amp; Warranties · Closing-Date Compliance</h2>
    <hr className="doc-rule" />

    <p>
      Pursuant to that certain <strong>Note Purchase &amp; Assignment Agreement</strong> dated <strong>{fmtDate(d.saleClosingDate)}</strong> (the <em>"Purchase Agreement"</em>) by and between <strong>{d.borrowerName}</strong> (<em>"Seller"</em>) and <strong>{d.buyerName}</strong> (<em>"Buyer"</em>), the undersigned, in their capacity as {d.signerTitle} of Seller and not in any individual capacity, hereby certifies to Buyer, as of the Closing Date set forth above, that:
    </p>

    <ol>
      <li>
        <span className="clause-title">Bring-Down of Representations.</span> Each and every representation and warranty of Seller set forth in the Purchase Agreement is true, correct, and complete in all material respects as of the date hereof with the same force and effect as though made on and as of the date hereof (except for representations expressly made as of an earlier date, which remain true as of such earlier date).
      </li>
      <li>
        <span className="clause-title">Covenants Performed.</span> Seller has duly performed, satisfied, and complied with each covenant, agreement, and condition required to be performed, satisfied, or complied with by Seller under the Purchase Agreement on or prior to the Closing Date.
      </li>
      <li>
        <span className="clause-title">No Litigation; No Default.</span> No action, suit, proceeding, or governmental investigation is pending or, to the knowledge of Seller, threatened that questions or could reasonably be expected to question the validity, legality, or enforceability of the Purchase Agreement, the Subject Note, the Allonge, the Bill of Sale, or the Notice to Buyer; and no Event of Default (as defined in the Subject Note) has occurred and is continuing.
      </li>
      <li>
        <span className="clause-title">Title to Subject Note.</span> Immediately prior to Closing, Seller was the sole legal and beneficial owner of the Subject Note, with full right, power, and authority to sell, assign, and transfer the same to Buyer, free and clear of all liens, security interests, pledges, claims, encumbrances, and adverse interests of every kind whatsoever. All prior pledges, security interests, or hypothecations affecting the Subject Note (if any) have been released of record on or before the date hereof.
      </li>
      <li>
        <span className="clause-title">Physical Delivery.</span> The original wet-ink Subject Note dated {fmtDate(d.executionDate)} in the face principal amount of {fmtCurrency(d.faceValue)}, together with the executed Allonge (Exhibit A) endorsed to the order of Buyer without recourse, has been physically delivered to Buyer or its designated custodian concurrently with the execution of this Certificate, in accordance with UCC §§ 3-201 and 3-203.
      </li>
      <li>
        <span className="clause-title">Authorization.</span> The execution and delivery of the Purchase Agreement, this Certificate, and each Transaction Document by Seller, and the consummation by Seller of the transactions contemplated thereby, have been duly authorized by all necessary corporate, limited-liability-company, or trust action; require no further approval; and do not contravene Seller's organizational documents or any law, contract, or order binding on Seller.
      </li>
      <li>
        <span className="clause-title">Wire Confirmation.</span> Seller hereby confirms that the Purchase Price of <span className="legal-words">{numberToWords(d.purchasePrice).toUpperCase()}</span> <strong>({fmtCurrency(d.purchasePrice)})</strong> has been (or is being concurrently) received by wire transfer of immediately available funds from Buyer in accordance with the wire instructions delivered at Closing, and Seller acknowledges full and final satisfaction of all consideration owed by Buyer with respect to the Subject Note.
      </li>
      <li>
        <span className="clause-title">OFAC / AML Confirmation.</span> The funds disbursed and received in this transaction are not the proceeds of any unlawful activity, and neither Seller nor any of its beneficial owners is a person subject to sanctions administered by the U.S. Department of the Treasury's Office of Foreign Assets Control.
      </li>
    </ol>

    <p style={{ fontStyle: "italic" }}>
      <strong>IN WITNESS WHEREOF,</strong> the undersigned has executed this Seller's Closing Certificate as of the Closing Date first written above.
    </p>

    <div className="sig-block">
      <div className="sig-col">
        <p><strong>SELLER:</strong></p>
        <p>{d.borrowerName}</p>
        <SignatureLine sig={findSig(d.signatures, "seller")} defaultName={d.signerName} />
        <div className="sig-line">Title: {d.signerTitle || "Authorized Officer"}</div>
        <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
      </div>
      <div className="sig-col">
        <p><strong>RECEIVED &amp; ACKNOWLEDGED · BUYER:</strong></p>
        <p>{d.buyerName}</p>
        <SignatureLine sig={findSig(d.signatures, "buyer")} defaultName={d.buyerSigner} />
        <div className="sig-line">Title: {d.buyerTitle || "Trustee"}</div>
      </div>
    </div>
    {findSig(d.signatures, "notary") && <InlineNotaryBlock d={d} signerName={d.borrowerName} />}
  </Paper>
));

// ============ DOCUMENT 06-E: Officer's Incumbency Certificate ============

export const Doc6EIncumbency = React.forwardRef(({ d }, ref) => (
  <Paper ref={ref} footer={<DocFooter label="Doc 06-E — Officer's Incumbency Certificate (Signature Authentication)" />}>
    <h2 className="doc-sub">Exhibit E · Outright Note Sale Closing Package</h2>
    <h1 className="doc-title">Officer's Incumbency Certificate</h1>
    <h2 className="doc-sub">Authentication of Authorized Signatory · Specimen Signature</h2>
    <hr className="doc-rule" />

    <p>
      The undersigned, <strong>{d.secretaryName}</strong>, in their capacity as Secretary (or equivalent records officer) of <strong>{d.borrowerName}</strong>, a {d.borrowerState} limited liability company (the <em>"Entity"</em>), and not in any individual capacity, does hereby certify, as of <strong>{fmtDate(d.saleClosingDate)}</strong>, on behalf of and for the benefit of <strong>{d.buyerName}</strong> and any successor holder of the Subject Note, as follows:
    </p>

    <ol>
      <li>
        <span className="clause-title">Good Standing.</span> The Entity is a limited liability company duly organized, validly existing, and in good standing under the laws of the State of {d.borrowerState}, with full power and authority to own its assets, to conduct its business, and to enter into and perform the Note Purchase &amp; Assignment Agreement and each Transaction Document executed in connection therewith.
      </li>
      <li>
        <span className="clause-title">Authorizing Resolutions.</span> The resolutions of the management of the Entity attached as <em>Schedule 1</em> hereto (the <em>"Resolutions"</em>) were duly adopted on or before {fmtDate(d.executionDate)}, have not been amended, modified, rescinded, or revoked, and remain in full force and effect as of the date hereof. The Resolutions specifically authorize the sale, transfer, assignment, and endorsement of the Subject Note to {d.buyerName} at a discounted purchase price.
      </li>
      <li>
        <span className="clause-title">Incumbent Officer.</span> The following individual is, and at all times relevant to the execution of the Purchase Agreement, the Subject Note endorsement, the Allonge, the Bill of Sale, the Notice to Buyer, and this Certificate has been, the duly elected, qualified, and acting officer of the Entity holding the office set forth opposite their name, and the signature appearing opposite their name is their genuine specimen signature:
      </li>
    </ol>

    <table className="kv-table" style={{ marginTop: 8, marginBottom: 18 }}>
      <tbody>
        <tr>
          <td style={{ fontWeight: 700 }}>Name</td>
          <td>{d.signerName}</td>
        </tr>
        <tr>
          <td style={{ fontWeight: 700 }}>Office / Title</td>
          <td>{d.signerTitle}</td>
        </tr>
        <tr>
          <td style={{ fontWeight: 700 }}>Specimen Signature</td>
          <td style={{ borderBottom: "1px solid #0A0A0A", height: 40, paddingTop: 4 }}>
            {findSig(d.signatures, "seller") ? (
              <span style={{ fontFamily: "'Brush Script MT','Lucida Handwriting','Apple Chancery',cursive", fontSize: "20pt", color: "#1a3a8e" }}>
                /s/ {(findSig(d.signatures, "seller") || {}).signer_name}
              </span>
            ) : (
              <span style={{ fontStyle: "italic", color: "#5C5C5C" }}>(signature: __________________________________)</span>
            )}
          </td>
        </tr>
        <tr>
          <td style={{ fontWeight: 700 }}>Authority Conferred</td>
          <td>Sole-signature authority to execute all instruments and certificates in connection with the sale of the Subject Note.</td>
        </tr>
      </tbody>
    </table>

    <ol start={4}>
      <li>
        <span className="clause-title">Reliance.</span> Buyer and any successor holder of the Subject Note are entitled to rely on this Certificate, including the specimen signature set forth above, until receipt by Buyer of written notice from the Entity, signed by an officer other than the incumbent above, of a revocation, modification, or supersession of the authorities granted herein.
      </li>
      <li>
        <span className="clause-title">No Adverse Proceedings.</span> No action, suit, or proceeding has been commenced, and to the knowledge of the undersigned no such action has been threatened, that would in any manner restrict, prohibit, or limit the consummation of the transactions contemplated by the Purchase Agreement or the validity of the Resolutions.
      </li>
    </ol>

    <p style={{ fontStyle: "italic" }}>
      <strong>IN WITNESS WHEREOF,</strong> the undersigned has executed and delivered this Officer's Incumbency Certificate as of the date first written above.
    </p>

    <div className="sig-block">
      <div className="sig-col">
        <p><strong>CERTIFIED BY (SECRETARY):</strong></p>
        <p>{d.borrowerName}</p>
        <SignatureLine sig={findSig(d.signatures, "trustee")} defaultName={d.secretaryName} />
        <div className="sig-line">Title: Secretary</div>
        <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
      </div>
      <div className="sig-col">
        <p><strong>COUNTER-CERTIFIED · INCUMBENT OFFICER:</strong></p>
        <p>I confirm the foregoing is true as to me:</p>
        <SignatureLine sig={findSig(d.signatures, "seller")} defaultName={d.signerName} />
        <div className="sig-line">Title: {d.signerTitle || "Authorized Officer"}</div>
        <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
      </div>
    </div>
  </Paper>
));

// ============ DOCUMENT T-01: Certificate of Trust (SPV) ============

export const Doc6FProceedsDirection = React.forwardRef(({ d }, ref) => {
  const toTrustee = d.proceedsRecipient !== "counterparty";
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc 06-F — Proceeds Direction & Disbursement Letter (UCC § 9-406 / Common-Law Assignment)" />}>
      <h2 className="doc-sub">Exhibit F · Outright Note Sale Closing Package</h2>
      <h1 className="doc-title">Proceeds Direction &amp; Disbursement Letter</h1>
      <h2 className="doc-sub">Trust's Election Under § 9-406 · Common-Law Assignment of Proceeds</h2>
      <hr className="doc-rule" />

      <p><strong>Date:</strong> {fmtDate(d.saleClosingDate)}</p>
      <p><strong>From:</strong> {d.trustName}, by its {d.trusteeTitle}</p>
      <p><strong>To:</strong> {d.buyerName} (the <em>"Buyer"</em>)</p>
      <p style={{ marginTop: 10 }}><strong>Re:</strong> Disbursement of Discount Proceeds under the Note Purchase &amp; Assignment Agreement dated {fmtDate(d.saleClosingDate)}</p>

      <hr className="doc-rule-thin" />

      <p>
        Reference is made to that certain Note Purchase &amp; Assignment Agreement dated <strong>{fmtDate(d.saleClosingDate)}</strong> (the <em>"Purchase Agreement"</em>) by and between <strong>{d.trustName}</strong> (the <em>"Trust"</em> or <em>"Seller"</em>) and <strong>{d.buyerName}</strong> (the <em>"Buyer"</em>), pursuant to which Buyer is purchasing from Seller the UCC Article 3 Negotiable Promissory Note dated {fmtDate(d.executionDate)} in the original face principal amount of {fmtCurrency(d.faceValue)} (the <em>"Subject Note"</em>) for an aggregate Purchase Price of <span className="legal-words">{numberToWords(d.purchasePrice).toUpperCase()}</span> <strong>({fmtCurrency(d.purchasePrice)})</strong>.
      </p>

      <p>
        The Trust hereby irrevocably elects, pursuant to <strong>Article V of the Trust Agreement</strong>, the Trust Resolution dated {fmtDate(d.trustExecutionDate)}, and the proceeds-direction authority of <strong>UCC § 9-406</strong> and common-law assignment of proceeds, that Buyer shall disburse the Purchase Price at Closing as follows:
      </p>

      <div style={{ border: "2px solid var(--boa-blue)", padding: "14px 18px", margin: "14px 0", background: "rgba(1,33,105,0.04)" }}>
        <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.22em", color: "var(--boa-red)", fontWeight: 800, marginBottom: 6 }}>
          Trust's Election — Option {toTrustee ? "A · Direct to Trust" : "B · Direct to Counterparty"}
        </div>
        {toTrustee ? (
          <div style={{ fontSize: "10.5pt", lineHeight: 1.55 }}>
            <strong>To the Trust's Operating Account.</strong> Buyer shall wire the full Purchase Price by Fedwire / CHIPS to the Trust's bank account specified below, for general Trust working capital, reserves, and authorized expenditures pursuant to <strong>{d.proceedsPurpose}</strong>.
          </div>
        ) : (
          <div style={{ fontSize: "10.5pt", lineHeight: 1.55 }}>
            <strong>To a Designated Counterparty.</strong> Buyer shall wire the full Purchase Price by Fedwire / CHIPS <strong>directly to the third-party counterparty designated below</strong>, for application to the obligation, transaction, or authorized purpose described herein. The Trust authorizes Buyer to disburse without further direction and represents that such disbursement shall fully and finally discharge Buyer's payment obligation under the Purchase Agreement.
          </div>
        )}
      </div>

      {toTrustee ? (
        <table className="kv-table">
          <tbody>
            <tr><td>Beneficiary Name</td><td><strong>{d.trustName}</strong></td></tr>
            {d.trustBankName && <tr><td>Beneficiary Bank</td><td>{d.trustBankName}</td></tr>}
            {d.trustAccountNumber && <tr><td>Account Number</td><td>{d.trustAccountNumber}</td></tr>}
            {!d.trustBankName && !d.trustAccountNumber && (
              <tr><td>Settlement Instructions</td><td><em>To be furnished by the Trust in writing under separate cover.</em></td></tr>
            )}
            <tr><td>Reference</td><td>Discount Sale of Subject Note · {fmtDate(d.saleClosingDate)}</td></tr>
            <tr><td>Stated Purpose</td><td>{d.proceedsPurpose}</td></tr>
          </tbody>
        </table>
      ) : (
        <table className="kv-table">
          <tbody>
            <tr><td>Beneficiary Name</td><td><strong>{d.counterpartyName}</strong></td></tr>
            {d.counterpartyAddress && <tr><td>Beneficiary Address</td><td>{d.counterpartyAddress}</td></tr>}
            <tr><td>Relationship to Trust</td><td>{d.counterpartyRole}</td></tr>
            {d.counterpartyBankName && <tr><td>Beneficiary Bank</td><td>{d.counterpartyBankName}</td></tr>}
            {d.counterpartyRouting && <tr><td>ABA Routing</td><td>{d.counterpartyRouting}</td></tr>}
            {d.counterpartyAccount && <tr><td>Account Number</td><td>{d.counterpartyAccount}</td></tr>}
            {!d.counterpartyBankName && !d.counterpartyAccount && (
              <tr><td>Settlement Instructions</td><td><em>To be furnished by the Counterparty in writing under separate cover.</em></td></tr>
            )}
            <tr><td>Reference</td><td>{d.trustName} · Discount Sale Proceeds · {fmtDate(d.saleClosingDate)}</td></tr>
            {d.counterpartyPurpose && <tr><td>Authorized Use</td><td>{d.counterpartyPurpose}</td></tr>}
          </tbody>
        </table>
      )}

      <p style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 18 }}>Buyer's Discharge &amp; Trust's Acknowledgments</p>

      <ol>
        <li>
          <span className="clause-title">Full Discharge.</span> Buyer's wire of the Purchase Price to the account designated above shall constitute full, final, and irrevocable payment and shall fully discharge Buyer's obligations under the Purchase Agreement and the Bill of Sale, in accordance with <strong>UCC § 9-406(a)</strong> and the common-law rule that payment to a designated assignee discharges the payment obligation.
        </li>
        <li>
          <span className="clause-title">No Inquiry Duty.</span> Buyer shall have no duty, obligation, or liability to inquire into the application of the Purchase Price by the recipient, the underlying transaction giving rise to the direction, or the relationship between the Trust and the designated recipient. The Trust agrees to <strong>indemnify and hold Buyer harmless</strong> from and against any claim arising from compliance with this direction.
        </li>
        <li>
          <span className="clause-title">Not a Loan, Line of Credit, or Extension of Credit.</span> The parties expressly acknowledge that the disbursement directed hereby is the payment of the Purchase Price for an outright asset sale and does <strong>NOT</strong> constitute (a) a loan, advance, or extension of credit by Buyer to the Trust or to the designated counterparty; (b) a commitment by Buyer to lend or to make any future advances; (c) a line of credit, revolving facility, or financing commitment of any kind; or (d) a guarantee by Buyer of any obligation owing between the Trust and the designated counterparty. Buyer's role is limited to that of a <strong>purchaser-for-value</strong> of a negotiable instrument under UCC Article 3.
        </li>
        <li>
          <span className="clause-title">Trust Authority.</span> The undersigned Trustee, acting alone under Article V of the Trust Agreement and the Trust Resolution dated {fmtDate(d.trustExecutionDate)}, has full power and authority to deliver this Direction Letter; no further consent, ratification, or co-trustee signature is required.
        </li>
        <li>
          <span className="clause-title">Irrevocability.</span> This Direction is <strong>IRREVOCABLE</strong> upon execution and may not be modified, rescinded, or countermanded except by a written instrument signed by both the Trust and Buyer.
        </li>
        <li>
          <span className="clause-title">Governing Law.</span> This Direction Letter shall be governed by the laws of the State of {d.governingLaw}.
        </li>
      </ol>

      <p style={{ fontStyle: "italic" }}>
        <strong>IN WITNESS WHEREOF,</strong> the undersigned Trustee has executed this Proceeds Direction &amp; Disbursement Letter as of the Closing Date first above written.
      </p>

      <div className="sig-block">
        <div className="sig-col">
          <p><strong>SPV TRUST (Directing Party):</strong></p>
          <p>{d.trustName}</p>
          <SignatureLine sig={findSig(d.signatures, "buyer") || findSig(d.signatures, "trustee")} defaultName={d.trusteeName} />
          <div className="sig-line">Title: {d.trusteeTitle || "Trustee"}</div>
          <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
        </div>
        <div className="sig-col">
          <p><strong>BUYER (Acknowledged &amp; Confirmed):</strong></p>
          <p>{d.buyerName}</p>
          <SignatureLine sig={findSig(d.signatures, "buyer")} defaultName={d.buyerSigner} />
          <div className="sig-line">Title: {d.buyerTitle || "Trustee"}</div>
          <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
        </div>
      </div>
      {findSig(d.signatures, "notary") && <InlineNotaryBlock d={d} signerName={d.trustName} />}

      {!toTrustee && (
        <div className="bankers-acceptance" style={{ marginTop: 24 }}>
          <span className="ba-stamp" style={{ background: "var(--boa-blue)" }}>Counterparty Receipt</span>
          <div className="ba-title">
            <span>Receipt &amp; Acknowledgment by Designated Counterparty</span>
            <span className="ba-tag">UCC § 9-406(a)</span>
          </div>
          <div className="ba-body">
            The undersigned, as authorized representative of <strong>{d.counterpartyName}</strong>, acknowledges receipt of the Purchase Price wired by {d.buyerName} pursuant to the Trust's irrevocable direction set forth herein, and confirms that such payment shall be applied in accordance with the Authorized Use stated above. The Counterparty releases Buyer from any further obligation with respect to said proceeds.
            <div className="ba-sig">
              <div>
                <div className="ba-line">By: ______________________</div>
                <div style={{ marginTop: 4, fontSize: "9.5pt" }}>{d.counterpartyName}</div>
              </div>
              <div>
                <div className="ba-line">Date Received: ____________</div>
                <div style={{ marginTop: 4, fontSize: "9.5pt" }}>Reference: {d.trustName}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Paper>
  );
});


// ============ DOCUMENT T-05: Custody / Bailee Agreement ============

export const Doc6GDisbursementAck = React.forwardRef(({ d }, ref) => {
  const isCounterparty = d.proceedsRecipient === "counterparty";
  const recipientName = isCounterparty ? d.counterpartyName : d.trustName;
  const recipientBank = isCounterparty ? d.counterpartyBankName : d.trustBankName;
  const recipientAccount = isCounterparty ? d.counterpartyAccount : d.trustAccountNumber;
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc 06-G — Disbursement Acknowledgment & Application Receipt (UCC § 9-406(a) Discharge)" />}>
      <h2 className="doc-sub">Exhibit G · Outright Note Sale Closing Package</h2>
      <h1 className="doc-title">Disbursement Acknowledgment &amp; Application Receipt</h1>
      <h2 className="doc-sub">Recipient's Confirmation of Receipt · Pro-Tanto Release · UCC § 9-406(a)</h2>
      <hr className="doc-rule" />

      <p><strong>Date:</strong> {fmtDate(d.saleClosingDate)}</p>
      <p><strong>Recipient:</strong> {recipientName}</p>
      <p><strong>Payor:</strong> {d.buyerName}</p>
      <p style={{ marginTop: 10 }}><strong>Re:</strong> Receipt of Discount-Sale Proceeds under Proceeds Direction Letter (Exhibit F) dated {fmtDate(d.saleClosingDate)}</p>

      <hr className="doc-rule-thin" />

      <p>
        The undersigned, in its capacity as Recipient under that certain Proceeds Direction &amp; Disbursement Letter (Exhibit F) dated <strong>{fmtDate(d.saleClosingDate)}</strong> issued by <strong>{d.trustName}</strong> (the <em>"Trust"</em>) and acknowledged by <strong>{d.buyerName}</strong> (<em>"Payor"</em>), hereby <strong>ACKNOWLEDGES AND CONFIRMS</strong> as of the date hereof that:
      </p>

      <ol>
        <li>
          <span className="clause-title">Receipt of Funds.</span> The undersigned has actually and unconditionally received from Payor, by Fedwire / CHIPS transfer of immediately available funds, the sum of <span className="legal-words">{numberToWords(d.purchasePrice).toUpperCase()}</span> <strong>({fmtCurrency(d.purchasePrice)})</strong> (the <em>"Disbursed Amount"</em>) into the following account:
        </li>
      </ol>

      <table className="kv-table">
        <tbody>
          <tr><td>Account Name</td><td><strong>{recipientName}</strong></td></tr>
          <tr><td>Receiving Bank</td><td>{recipientBank}</td></tr>
          <tr><td>Account Number</td><td>{recipientAccount}</td></tr>
          <tr><td>Wire Reference</td><td>{d.trustName} · Discount Sale of Subject Note · {fmtDate(d.saleClosingDate)}</td></tr>
        </tbody>
      </table>

      <ol start={2}>
        <li>
          <span className="clause-title">Application of Funds.</span> The Disbursed Amount has been applied (or shall be applied no later than the next Business Day) by the undersigned to the following obligation(s) or purpose(s):
          <div style={{ border: "1px solid var(--boa-blue)", padding: "10px 14px", marginTop: 8, background: "rgba(1,33,105,0.03)", fontSize: "10.5pt", lineHeight: 1.55 }}>
            <strong>Authorized Use:</strong>&nbsp; {isCounterparty ? d.counterpartyPurpose : d.proceedsPurpose}
            {isCounterparty && (
              <>
                <br /><strong>Underlying Obligation / Relationship:</strong>&nbsp; {d.counterpartyRole}
              </>
            )}
          </div>
        </li>
        <li>
          <span className="clause-title">Pro-Tanto Release of Trust.</span> {isCounterparty ? (
            <>To the extent that the Disbursed Amount has been applied against an existing debt, obligation, or commitment owed by the Trust (or by {d.trustSettlor} on behalf of the Trust) to the undersigned, the undersigned hereby <strong>RELEASES AND DISCHARGES</strong> the Trust and {d.trustSettlor} <em>pro tanto</em> (to the extent of the Disbursed Amount) from such debt, obligation, or commitment, and the books and records of the undersigned shall be conformed accordingly.</>
          ) : (
            <>The Disbursed Amount has been credited to the Trust's general operating account as working capital and reserves and is held by the Trust subject to the further written instructions of the {d.trusteeTitle}.</>
          )}
        </li>
        <li>
          <span className="clause-title">Payor Fully Discharged (UCC § 9-406(a)).</span> The undersigned acknowledges that Payor's wire of the Disbursed Amount to the account designated in the Proceeds Direction Letter constitutes <strong>full and final discharge</strong> of Payor's payment obligation under the Note Purchase &amp; Assignment Agreement dated {fmtDate(d.saleClosingDate)}. The undersigned shall have no claim against Payor for any further amount on account of the Subject Note or the Purchase Price, and the undersigned shall <strong>indemnify and hold Payor harmless</strong> from any claim by any party that may assert a competing right to the Disbursed Amount.
        </li>
        <li>
          <span className="clause-title">Not a Loan, Line of Credit, or Extension of Credit.</span> The undersigned acknowledges that Payor's payment hereunder is consideration paid for the outright purchase of a UCC Article 3 negotiable instrument, and does <strong>NOT</strong> constitute a loan, advance, extension of credit, or financing commitment by Payor to the Trust, to the undersigned, or to any other person, and creates no continuing relationship between Payor and the undersigned beyond this Receipt.
        </li>
        <li>
          <span className="clause-title">Authority of Undersigned.</span> The individual signing below has full power and authority to execute this Disbursement Acknowledgment on behalf of the named Recipient, and the Recipient has received any internal credit-committee, board, or governance approvals required for the application of the Disbursed Amount as described above.
        </li>
        <li>
          <span className="clause-title">Reliance.</span> This Disbursement Acknowledgment may be relied upon by Payor, by the Trust, by any successor holder of the Subject Note, and by any audit, tax, or regulatory authority reviewing the closing of the underlying transaction.
        </li>
      </ol>

      <p style={{ fontStyle: "italic", marginTop: 14 }}>
        <strong>IN WITNESS WHEREOF,</strong> the undersigned Recipient has executed this Disbursement Acknowledgment &amp; Application Receipt as of the date first above written.
      </p>

      <div className="sig-block">
        <div className="sig-col">
          <p><strong>RECIPIENT:</strong></p>
          <p>{recipientName}</p>
          <SignatureLine sig={findSig(d.signatures, "seller")} defaultName="" />
          <div className="sig-line">Title: Authorized Officer</div>
          <div className="sig-line">Date Received: {fmtDate(d.saleClosingDate)}</div>
        </div>
        <div className="sig-col">
          <p><strong>TRUST (Acknowledged):</strong></p>
          <p>{d.trustName}</p>
          <SignatureLine sig={findSig(d.signatures, "buyer") || findSig(d.signatures, "trustee")} defaultName={d.trusteeName} />
          <div className="sig-line">Title: {d.trusteeTitle || "Trustee"}</div>
          <div className="sig-line">Date: {fmtDate(d.saleClosingDate)}</div>
        </div>
      </div>
    </Paper>
  );
});



// ============ DOCUMENT T-06: True-Sale Opinion Letter (Counsel Opinion) ============
// Independent legal opinion from outside counsel (Beale & Drape LLP) opining that
// the Originator → Trust conveyance is a "true sale" and that the Trust is
// bankruptcy-remote. Standard piece in any securitization or SPV closing binder.

